// packages/web/src/routes/_authed/admin/uren.$entryId.edit.tsx
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { AdminShell } from "@/components/layout/admin-shell";
import { splitName } from "@/lib/split-name";
import { appSidebarSections, appBottomNavItems } from "@/lib/app-sidebar";
import { hoursEntryByIdOptions } from "@/api/hours-entries/query-options";
import { workordersListOptions, craneByWorkorderOptions } from "@/api/workorders/query-options";
import { usersByIdOptions } from "@/api/users/query-options";
import { dayBreakByUserDateOptions } from "@/api/day-breaks/query-options";
import {
  EntryEditForm,
  type EntryEditFormInitial,
} from "@/components/admin/uren-edit/entry-edit-form";
import type { RoleKind } from "@/components/vroom/role-pill";

export const Route = createFileRoute("/_authed/admin/uren/$entryId/edit")({
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string } }).user;
    if (user?.role !== "super_admin" && user?.role !== "approver") {
      throw redirect({ to: "/work", search: {} });
    }
  },
  component: UrenEdit,
});

interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: RoleKind;
}

export function UrenEdit() {
  const { t } = useTranslation();
  const { user: sessionUser } = Route.useRouteContext() as { user: SessionUser };
  const { firstName, lastName } = splitName(sessionUser.name);
  const { entryId } = Route.useParams();
  const entryQ = useSuspenseQuery(hoursEntryByIdOptions(entryId));
  const woQ = useSuspenseQuery(workordersListOptions({ includeDeleted: "false" }));

  const e = entryQ.data;

  const cranesQ = useQuery({
    ...craneByWorkorderOptions(e.workorderId ?? ""),
    enabled: !!e.workorderId,
  });

  const workerQ = useQuery(usersByIdOptions(e.userId));
  const dayBreakQ = useQuery(dayBreakByUserDateOptions(e.userId, e.date));

  const dayBreak = dayBreakQ.data ?? null;

  const initial: EntryEditFormInitial = {
    entryId: e.id,
    startAt: typeof e.startAt === "number" ? e.startAt : new Date(e.startAt).getTime(),
    endAt: typeof e.endAt === "number" ? e.endAt : new Date(e.endAt).getTime(),
    workorderId: e.workorderId ?? null,
    craneId: e.craneId ?? null,
    note: e.note ?? "",
    dayBreakId: dayBreak ? `${e.userId}:${e.date}` : null,
    dayBreakMinutes: dayBreak?.breakDurationMinutes ?? 0,
    status: e.status,
    workerName: workerQ.data?.name ?? "—",
    workerId: e.userId,
    date: e.date,
    actorIsSuperAdmin: sessionUser.role === "super_admin",
    actorIsApprover: sessionUser.role === "approver",
    approverUserId: e.approverUserId,
  };
  const workorderOptions = (woQ.data?.data ?? []).map((w) => ({
    id: w.id,
    number: w.erpCode,
  }));
  const craneOptions = (cranesQ.data ?? []).map((c) => ({
    id: c.id,
    number: c.erpCode,
  }));

  return (
    <AdminShell
      user={{ firstName, lastName, role: sessionUser.role }}
      section="admin"
      breadcrumb={<>{t("admin.uren.breadcrumb")}</>}
      sidebarSections={appSidebarSections({ active: "uren", role: sessionUser.role }, t)}
      bottomNavItems={appBottomNavItems(
        { section: "admin", active: "uren", role: sessionUser.role },
        t,
      )}
    >
      <EntryEditForm
        initial={initial}
        workorderOptions={workorderOptions}
        craneOptions={craneOptions}
        actorUserId={sessionUser.id}
      />
    </AdminShell>
  );
}
